import {
  FacetedFilterOption,
} from "@/components/button-faceted-filter";
import { getSelectedTeamId } from "@/hooks/useApi";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey, type CompanyRefusalKey } from "@/lib/company-refusal";
import {
  COMPANY_TAB_READ_QUERY_OPTIONS,
  companyTabReads,
  type CompanyTabRead,
} from "@/lib/company-tab-reads";
import {
  getLogoPlatformIcon,
  getPlatformBrandColor,
  getReviewPlatformIcon,
} from "@/lib/platform-brand-colors";
import {
  CompanyDTO,
  CompanyReviewDTO,
  CompanyReviewPlatforms,
  CompanySocialNetworksActivityDTO,
  CompanySocialNetworksPlatforms,
  JobOfferDTO,
  JobOfferPlatform,
} from "@shaarp/common";
import { useEffect, useMemo, useState } from "react";
import {
  RiExternalLinkFill,
  RiFacebookBoxFill,
  RiLinkedinBoxFill,
  RiMegaphoneLine,
  RiTwitterXLine,
} from "react-icons/ri";
import { SiGlassdoor, SiIndeed, SiWelcometothejungle } from "react-icons/si";

export type SocialNetworkActivity = Omit<CompanySocialNetworksActivityDTO, "date"> & {
  icon: React.ComponentType<{ className?: string }>;
  author: string | undefined;
  date: string;
};

export type Review = Omit<CompanyReviewDTO, "date"> & {
  icon: React.ComponentType<{ className?: string }>;
  date: string;
};

export type JobOfferEnriched = Omit<JobOfferDTO, "createdAt"> & {
  icon: React.ComponentType<{ className?: string }>;
  platformName: string;
  date: string;
};

function getSocialIcon(
  platform: CompanySocialNetworksPlatforms,
): React.ComponentType<{ className?: string }> {
  const logoIcon = getLogoPlatformIcon(platform);
  if (logoIcon) return logoIcon;

  switch (platform) {
    case "linkedin":
      return RiLinkedinBoxFill;
    case "x":
      return RiTwitterXLine;
    case "facebook":
      return RiFacebookBoxFill;
    default:
      return RiExternalLinkFill;
  }
}

function getSocialAuthor(
  platform: CompanySocialNetworksPlatforms,
  company: CompanyDTO,
): string | undefined {
  switch (platform) {
    case "instagram":
      return company.socialNetworks?.instagram?.handle ?? undefined;
    case "linkedin":
      return company.socialNetworks?.linkedin?.handle ?? undefined;
    case "x":
      return company.socialNetworks?.x?.handle ?? undefined;
    case "facebook":
      return company.socialNetworks?.facebook?.handle ?? undefined;
    default:
      return undefined;
  }
}

function getJobOfferIcon(
  platform: JobOfferPlatform,
): React.ComponentType<{ className?: string }> {
  const logoDevIcon = getLogoPlatformIcon(platform);
  if (logoDevIcon) return logoDevIcon;

  switch (platform) {
    case "indeed":
      return SiIndeed;
    case "linkedin":
      return RiLinkedinBoxFill;
    case "glassdoor":
      return SiGlassdoor;
    case "wttj":
      return SiWelcometothejungle;
    default:
      return RiMegaphoneLine;
  }
}

function getJobOfferPlatformName(
  platform: JobOfferPlatform,
  otherLabel: string,
): string {
  switch (platform) {
    case "indeed":
      return "Indeed";
    case "linkedin":
      return "LinkedIn Jobs";
    case "glassdoor":
      return "Glassdoor";
    case "wttj":
      return "Welcome to the Jungle";
    case "france_travail":
      return "France Travail";
    case "hellowork":
      return "HelloWork";
    case "other":
      return otherLabel;
    default:
      return platform;
  }
}

function buildPlatformOptions<T extends string>(
  items: { platform: T }[],
  getIcon: (platform: T) => React.ComponentType<{ className?: string }>,
  getLabel: (platform: T) => string,
): FacetedFilterOption[] {
  const uniquePlatforms = [...new Set(items.map((item) => item.platform))];
  return uniquePlatforms.map((platform) => ({
    label: getLabel(platform),
    value: platform,
    icon: getIcon(platform),
    iconClassName: getPlatformBrandColor(platform),
    total: items.filter((item) => item.platform === platform).length,
  }));
}

function getSocialPlatformLabel(
  platform: CompanySocialNetworksPlatforms,
): string {
  switch (platform) {
    case "instagram":
      return "Instagram";
    case "linkedin":
      return "LinkedIn";
    case "x":
      return "X";
    case "facebook":
      return "Facebook";
    default:
      return platform;
  }
}

function getReviewPlatformLabel(platform: CompanyReviewPlatforms): string {
  switch (platform) {
    case "google":
      return "Google";
    default:
      return platform;
  }
}

type StoredPost = Omit<SocialNetworkActivity, "author">;

type UseCompanyNewsDataReturn = {
  posts: SocialNetworkActivity[];
  isLoadingSocials: boolean;
  socialPlatformsOptions: FacetedFilterOption[];
  socialPlatformsSelected: CompanySocialNetworksPlatforms[];
  setSocialPlatformsSelected: (value: CompanySocialNetworksPlatforms[]) => void;
  reviews: Review[];
  isLoadingReviews: boolean;
  reviewPlatformsOptions: FacetedFilterOption[];
  reviewPlatformsSelected: CompanyReviewPlatforms[];
  setReviewPlatformsSelected: (value: CompanyReviewPlatforms[]) => void;
  jobOffers: JobOfferEnriched[];
  isLoadingJobs: boolean;
  jobPlatformsOptions: FacetedFilterOption[];
  jobPlatformsSelected: JobOfferPlatform[];
  setJobPlatformsSelected: (value: JobOfferPlatform[]) => void;
  /**
   * A read the API refused (the team's daily tab reads, a throttle): its
   * message, so an empty list does not read as "nothing found".
   */
  refusal: CompanyRefusalKey | null;
};

/** One read of the news tab, served from the cache the page's prefetch fills. */
function useCompanyTabRead<T>(read: CompanyTabRead, isEnabled: boolean) {
  return useApiQuery<T>({
    key: read.key,
    path: read.path,
    enabled: isEnabled,
    ...COMPANY_TAB_READ_QUERY_OPTIONS,
  });
}

/** A list's platform filter: every platform it holds is selected when the list arrives. */
function useAllPlatformsSelected<T extends string>(
  items: readonly { platform: T }[],
): [T[], (value: T[]) => void] {
  const [selected, setSelected] = useState<T[]>([]);
  useEffect(() => {
    setSelected([...new Set(items.map((item) => item.platform))]);
  }, [items]);
  return [selected, setSelected];
}

/**
 * Fetches and enriches social-network posts, customer reviews, and job offers
 * for a given company. Returns derived platform-filter state alongside the
 * raw enriched lists so callers can drive both display and filtering from one
 * place.
 */
export function useCompanyNewsData(
  companyId: string,
  company: CompanyDTO,
  /**
   * False while the page's run still enriches what these reads return: they
   * wait for it rather than ask the same providers a second time, and read
   * what it wrote.
   */
  isEnabled = true,
): UseCompanyNewsDataReturn {
  const { t } = useTranslation();
  const otherJobPlatformLabel = t("company.jobOffers.platformOther");

  // The page prefetches these reads as the user reaches for the tab, once
  // their sections settle (`usePrefetchCompanyTabs`): opening it reads the cache.
  const reads = companyTabReads(getSelectedTeamId(), companyId);
  const socialsQuery = useCompanyTabRead<CompanySocialNetworksActivityDTO[]>(
    reads.socialActivities,
    isEnabled,
  );
  const reviewsQuery = useCompanyTabRead<CompanyReviewDTO[]>(
    reads.reviews,
    isEnabled,
  );
  const jobsQuery = useCompanyTabRead<JobOfferDTO[]>(reads.jobOffers, isEnabled);

  const storedPosts = useMemo<StoredPost[]>(
    () =>
      (socialsQuery.data ?? []).map((post) => ({
        ...post,
        icon: getSocialIcon(post.platform),
        date: new Date(post.date).toLocaleString(),
      })),
    [socialsQuery.data],
  );
  const reviews = useMemo<Review[]>(
    () =>
      (reviewsQuery.data ?? []).map((review) => ({
        ...review,
        icon: getReviewPlatformIcon(review.platform),
        date: new Date(review.date).toLocaleString(),
      })),
    [reviewsQuery.data],
  );
  const jobOffers = useMemo<JobOfferEnriched[]>(
    () =>
      (jobsQuery.data ?? []).map((jobOffer) => ({
        ...jobOffer,
        icon: getJobOfferIcon(jobOffer.platform),
        platformName: getJobOfferPlatformName(
          jobOffer.platform,
          otherJobPlatformLabel,
        ),
        date: new Date(jobOffer.createdAt).toLocaleDateString(),
      })),
    [jobsQuery.data, otherJobPlatformLabel],
  );

  const socialPlatformsOptions = useMemo(
    () =>
      buildPlatformOptions(storedPosts, getSocialIcon, getSocialPlatformLabel),
    [storedPosts],
  );
  const reviewPlatformsOptions = useMemo(
    () =>
      buildPlatformOptions(
        reviews,
        getReviewPlatformIcon,
        getReviewPlatformLabel,
      ),
    [reviews],
  );
  const jobPlatformsOptions = useMemo(
    () =>
      buildPlatformOptions(jobOffers, getJobOfferIcon, (platform) =>
        getJobOfferPlatformName(platform, otherJobPlatformLabel),
      ),
    [jobOffers, otherJobPlatformLabel],
  );

  const [socialPlatformsSelected, setSocialPlatformsSelected] =
    useAllPlatformsSelected(storedPosts);
  const [reviewPlatformsSelected, setReviewPlatformsSelected] =
    useAllPlatformsSelected(reviews);
  const [jobPlatformsSelected, setJobPlatformsSelected] =
    useAllPlatformsSelected(jobOffers);

  const isLoadingSocials = socialsQuery.isPending;
  const isLoadingReviews = reviewsQuery.isPending;
  const isLoadingJobs = jobsQuery.isPending;

  const refusal =
    [socialsQuery.error, reviewsQuery.error, jobsQuery.error]
      .map((error) => (error ? companyRefusalKey(error) : null))
      .find((key) => key !== null) ?? null;

  const posts = storedPosts.map((post) => ({
    ...post,
    author: getSocialAuthor(post.platform, company),
  }));

  return {
    posts,
    isLoadingSocials,
    socialPlatformsOptions,
    socialPlatformsSelected,
    setSocialPlatformsSelected,
    reviews,
    isLoadingReviews,
    reviewPlatformsOptions,
    reviewPlatformsSelected,
    setReviewPlatformsSelected,
    jobOffers,
    isLoadingJobs,
    jobPlatformsOptions,
    jobPlatformsSelected,
    setJobPlatformsSelected,
    refusal,
  };
}
